import { useState } from "react";
import { usePrimeStream } from "../api/primeStream";
import { useBriefingStream } from "../api/briefingStream";
import { useAgentStream } from "../api/agentStream";
import { useSearchRuns, useSearchRunDetail, type SearchRun } from "../api/search";
import { MessageBlock, type MessageBlockItem, type ContentFilter } from "../components/MessageBlock";
import { formatRelative } from "../components/formatting";

interface ColumnConfig {
  key: string;
  label: string;
  defaultVisible: boolean;
}

const COLUMNS: ColumnConfig[] = [
  { key: "prime", label: "Prime", defaultVisible: true },
  { key: "briefing", label: "Briefing", defaultVisible: true },
  { key: "scheduler", label: "Scheduler", defaultVisible: false },
  { key: "family", label: "Family", defaultVisible: false },
  { key: "search", label: "Search", defaultVisible: false },
];

const CONTENT_FILTERS: { key: ContentFilter; label: string; defaultOn: boolean }[] = [
  { key: "text", label: "Text", defaultOn: true },
  { key: "tool_use", label: "Tool Use", defaultOn: true },
  { key: "tool_result", label: "Tool Results", defaultOn: false },
  { key: "thinking", label: "Thinking", defaultOn: false },
  { key: "system", label: "System", defaultOn: true },
];

interface AgentColumnProps {
  title: string;
  items: MessageBlockItem[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  onLoadMore: () => void;
  isFetchingMore: boolean;
  contentFilters: Set<ContentFilter>;
}

function AgentColumn({
  title,
  items,
  isLoading,
  error,
  hasMore,
  onLoadMore,
  isFetchingMore,
  contentFilters,
}: AgentColumnProps) {
  return (
    <div className="activity-column">
      <div className="activity-column-header">
        <span>{title}</span>
        <span className="activity-count-badge">{items.length}</span>
      </div>
      <div className="activity-column-body">
        {isLoading && <div className="status-line">Loading...</div>}
        {error && (
          <div className="status-line error">
            {error instanceof Error ? error.message : String(error)}
          </div>
        )}
        {!isLoading && !error && items.length === 0 && (
          <div className="status-line">No activity yet.</div>
        )}
        {items.map((item) => (
          <MessageBlock key={item.id} item={item} contentFilters={contentFilters} />
        ))}
        {hasMore && (
          <div className="activity-load-more">
            <button
              className="btn btn-sm"
              onClick={onLoadMore}
              disabled={isFetchingMore}
            >
              {isFetchingMore ? "Loading..." : "Load older"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PrimeColumn({ contentFilters }: { contentFilters: Set<ContentFilter> }) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    usePrimeStream({ limit: 30 });

  const items: MessageBlockItem[] =
    data?.pages.flatMap((p) =>
      p.items.map((item) => ({
        ...item,
        author: item.author as MessageBlockItem["author"],
      }))
    ) ?? [];

  return (
    <AgentColumn
      title="Prime"
      items={items}
      isLoading={isLoading}
      error={error}
      hasMore={hasNextPage ?? false}
      onLoadMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      contentFilters={contentFilters}
    />
  );
}

function BriefingColumn({ contentFilters }: { contentFilters: Set<ContentFilter> }) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    useBriefingStream({ limit: 30 });

  const items: MessageBlockItem[] =
    data?.pages.flatMap((p) =>
      p.items.map((item) => ({
        ...item,
        author: item.author as MessageBlockItem["author"],
      }))
    ) ?? [];

  return (
    <AgentColumn
      title="Briefing"
      items={items}
      isLoading={isLoading}
      error={error}
      hasMore={hasNextPage ?? false}
      onLoadMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      contentFilters={contentFilters}
    />
  );
}

function GenericAgentColumn({ lineage, label, contentFilters }: { lineage: string; label: string; contentFilters: Set<ContentFilter> }) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    useAgentStream(lineage, 30);

  const items: MessageBlockItem[] =
    data?.pages.flatMap((p) =>
      p.items.map((item) => ({
        ...item,
        author: item.author as MessageBlockItem["author"],
      }))
    ) ?? [];

  return (
    <AgentColumn
      title={label}
      items={items}
      isLoading={isLoading}
      error={error}
      hasMore={hasNextPage ?? false}
      onLoadMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      contentFilters={contentFilters}
    />
  );
}

function SearchRunCard({ run, isExpanded, onToggle }: { run: SearchRun; isExpanded: boolean; onToggle: () => void }) {
  const { data: detail } = useSearchRunDetail(isExpanded ? run.id : null);
  const stats = run.stats as Record<string, number> | null;

  return (
    <div className={`search-run-card ${isExpanded ? "expanded" : ""}`} onClick={onToggle}>
      <div className="search-run-card-header">
        <div className="search-run-card-query">"{run.query}"</div>
        <div className="search-run-card-meta">
          <span className={`search-mode-badge ${run.mode}`}>{run.mode}</span>
          <span className="search-sources-badge">{run.sources}</span>
          <span className={`search-status-badge ${run.status}`}>{run.status}</span>
        </div>
        <div className="search-run-card-footer">
          <span className="search-run-card-stats">
            {stats?.queries_run && <span>{stats.queries_run}q</span>}
            {stats?.threads_fetched && <span>{stats.threads_fetched}t</span>}
            {stats?.pages_fetched && <span>{stats.pages_fetched}p</span>}
          </span>
          <span className="search-run-card-time">{formatRelative(run.created_at)}</span>
        </div>
      </div>
      {isExpanded && detail && (
        <div className="search-run-card-detail" onClick={(e) => e.stopPropagation()}>
          {detail.synthesis_text && (
            <pre className="search-run-card-synthesis">{detail.synthesis_text}</pre>
          )}
          {detail.raw_dumps.length > 0 && (
            <details className="search-run-card-raw">
              <summary>Raw data ({detail.raw_dumps.length} files)</summary>
              {detail.raw_dumps.map((dump) => (
                <details key={dump.id}>
                  <summary>{dump.filename}</summary>
                  <pre>{dump.content}</pre>
                </details>
              ))}
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function SearchColumn() {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useSearchRuns(20);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const runs = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="activity-column">
      <div className="activity-column-header">
        <span>Search</span>
        <span className="activity-count-badge">{runs.length}</span>
      </div>
      <div className="activity-column-body">
        {isLoading && <div className="status-line">Loading...</div>}
        {!isLoading && runs.length === 0 && (
          <div className="status-line">No searches yet.</div>
        )}
        {runs.map((run) => (
          <SearchRunCard
            key={run.id}
            run={run}
            isExpanded={expandedId === run.id}
            onToggle={() => setExpandedId(expandedId === run.id ? null : run.id)}
          />
        ))}
        {hasNextPage && (
          <div className="activity-load-more">
            <button
              className="btn btn-sm"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Loading..." : "Load older"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function AgentActivityView() {
  const [visible, setVisible] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(COLUMNS.map((c) => [c.key, c.defaultVisible]))
  );
  const [contentFilters, setContentFilters] = useState<Set<ContentFilter>>(
    () => new Set(CONTENT_FILTERS.filter((f) => f.defaultOn).map((f) => f.key))
  );

  function toggle(key: string) {
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleFilter(key: ContentFilter) {
    setContentFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const visibleColumns = COLUMNS.filter((c) => visible[c.key]);

  return (
    <div className="activity-view">
      <div className="activity-toggles">
        {COLUMNS.map((col) => (
          <button
            key={col.key}
            className={`activity-toggle ${visible[col.key] ? "active" : ""}`}
            onClick={() => toggle(col.key)}
          >
            {col.label}
          </button>
        ))}
      </div>
      <div className="content-toggles">
        <span className="content-toggles-label">Show:</span>
        {CONTENT_FILTERS.map((f) => (
          <button
            key={f.key}
            className={`content-toggle ${contentFilters.has(f.key) ? "active" : ""}`}
            onClick={() => toggleFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="activity-columns">
        {visibleColumns.map((col) => {
          if (col.key === "prime") return <PrimeColumn key={col.key} contentFilters={contentFilters} />;
          if (col.key === "briefing") return <BriefingColumn key={col.key} contentFilters={contentFilters} />;
          if (col.key === "search") return <SearchColumn key={col.key} />;
          return (
            <GenericAgentColumn
              key={col.key}
              lineage={col.key}
              label={col.label}
              contentFilters={contentFilters}
            />
          );
        })}
      </div>
    </div>
  );
}
